import React, { useEffect, useState } from "react";
import { apiRequest } from "../api";
import countries from "../data/countries.json";

const AddMultiEventModal = ({ tourId, event, onClose, onSave }) => {
    const [name, setName] = useState("");
    const [city, setCity] = useState("");
    const [country, setCountry] = useState("");
    const [formId, setFormId] = useState("");
    const [eventDate, setEventDate] = useState("");
    const [forms, setForms] = useState([]);
    const [feedbackMessage, setFeedbackMessage] = useState(null);
    const [universities, setUniversities] = useState([]);
    const [selectedUniversities, setSelectedUniversities] = useState([]);
    const [isLoadingUniversities, setIsLoadingUniversities] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false); // New state to track submission

    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem("token");

    // Fetch Forms & Universities on Mount
    useEffect(() => {
        const fetchForms = async () => {
            try {
                const data = await apiRequest(`${apiUrl}/form`, "GET", null, token);
                setForms(data);
            } catch (error) {
                console.error("Error fetching forms:", error);
                setFeedbackMessage("Failed to fetch forms. Please try again.");
            }
        };

        const fetchUniversities = async () => {
            setIsLoadingUniversities(true);
            try {
                const response = await fetch(`${apiUrl}/my-universities`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (!response.ok) throw new Error("Failed to fetch universities.");
                const data = await response.json();
                setUniversities(data);
            } catch (error) {
                console.error("Error fetching universities:", error);
                setUniversities([]);
            } finally {
                setIsLoadingUniversities(false);
            }
        };

        fetchForms();
        fetchUniversities();
    }, [apiUrl, token]);

    // Populate fields if editing
    useEffect(() => {
        if (event) {
            setName(event.name);
            setCity(event.city);
            setCountry(event.country || "");
            setFormId(event.form_id);
            setEventDate(event.event_date || "");

            if (event.universities) {
                setSelectedUniversities(event.universities.map((uni) => uni.id));
            }
        } else {
            setName("");
            setCity("");
            setCountry("");
            setFormId("");
            setEventDate("");
            setSelectedUniversities([]);
        }
    }, [event]);

    // Handle multi-select for universities
    const handleUniversityChange = (e) => {
        const selectedOptions = Array.from(e.target.selectedOptions, (option) => option.value);
        setSelectedUniversities(selectedOptions);
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true); // Start submission state
        setFeedbackMessage("Saving..."); // Show saving message

        // Ensure the event date is valid (ignoring time)
        const selectedDate = new Date(eventDate);
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);
        selectedDate.setHours(0, 0, 0, 0);

        if (selectedDate < currentDate) {
            setFeedbackMessage("Event date must be in the future.");
            setIsSubmitting(false);
            return;
        }

        const payload = {
            name,
            city: city,
            country: country || null,
            event_date: eventDate,
            tour_id: tourId,
            form_id: formId,
            university_ids: selectedUniversities.map(String),
        };

        const isUpdating = Boolean(event);
        const url = isUpdating ? `${apiUrl}/events/${event.id}` : `${apiUrl}/multi-events`;
        const method = isUpdating ? "PUT" : "POST";

        try {
            const response = await apiRequest(url, method, payload, token);

            if (isUpdating) {
                setFeedbackMessage("Event updated successfully!");
            } else {
                const numEvents = response.num_events_created; // Ensure correct key usage
                const success = response.success; // Check if operation was successful

                if (success) {
                    setFeedbackMessage(`${numEvents} event(s) created successfully!`);
                } else {
                    setFeedbackMessage("No new events were created.");
                }
            }

            setTimeout(() => {
                setFeedbackMessage(null);
                onSave();
                onClose();
            }, 2000);
        } catch (error) {
            console.error(`Error ${isUpdating ? "updating" : "creating"} event:`, error);
            setFeedbackMessage(`Failed to ${isUpdating ? "update" : "create"} event. Please try again.`);
        } finally {
            setIsSubmitting(false); // Stop submission state
        }
    };

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000,
            }}
        >
            <div
                style={{
                    backgroundColor: "#fff",
                    padding: "20px",
                    borderRadius: "5px",
                    width: "400px",
                }}
            >
                <h4>{event ? "Edit Event" : "Add New Event"}</h4>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Event Name:</label>
                        <input
                            type="text"
                            className="form-control"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            disabled={isSubmitting}
                        />
                    </div>
                    <div className="form-group">
                        <label>Event City:</label>
                        <input
                            type="text"
                            className="form-control"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            disabled={isSubmitting}
                        />
                    </div>
                    <div className="form-group">
                        <label>Event Country (Optional):</label>
                        <select
                            className="form-control"
                            value={country}
                            onChange={(e) => setCountry(e.target.value)}
                            disabled={isSubmitting}
                        >
                            <option value="">-- Select a Country --</option>
                            {countries.map((c) => (
                                <option key={c.name} value={c.name}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Event Date:</label>
                        <input
                            type="date"
                            className="form-control"
                            value={eventDate}
                            onChange={(e) => setEventDate(e.target.value)}
                            required
                            disabled={isSubmitting}
                        />
                    </div>

                    <div className="form-group">
                        <label>Select Form:</label>
                        <select
                            className="form-control"
                            value={formId}
                            onChange={(e) => setFormId(e.target.value)}
                            required
                            disabled={isSubmitting}
                        >
                            <option value="" disabled>
                                -- Select a Form --
                            </option>
                            {forms.map((form) => (
                                <option key={form.id} value={form.id}>
                                    {form.name}
                                </option>
                            ))}
                        </select>
                    </div>



                    <div className="form-group">
                        <label>Universities</label>
                        <select
                            className="form-control"
                            multiple
                            value={selectedUniversities}
                            onChange={handleUniversityChange}
                            disabled={isSubmitting}
                        >
                            {isLoadingUniversities ? (
                                <option>Loading...</option>
                            ) : (
                                universities.map((uni) => (
                                    <option key={uni.id} value={uni.id}>
                                        {uni.name}
                                    </option>
                                ))
                            )}
                        </select>
                        <small>Hold CTRL (Windows) or CMD (Mac) to select multiple</small>
                    </div>

                    <div className="modal-footer">
                        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                            {isSubmitting ? "Saving..." : event ? "Update" : "Save"}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
                            Cancel
                        </button>
                    </div>
                </form>
                {feedbackMessage && (
                    <p
                        style={{
                            marginTop: "10px",
                            color: feedbackMessage.includes("successfully") ? "green" : "red",
                        }}
                    >
                        {feedbackMessage}
                    </p>
                )}
            </div>
        </div>
    );
};

export default AddMultiEventModal;